import { useLocation } from "@tanstack/react-router";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";

import {
  RootSessionStripProjectionProvider,
  useRootSessionStripProjection,
} from "../rootSessionStripContainer";
import { useWorldView } from "../neucleos/worldView";
import { worldViewFrameKind } from "../worldViewRoute";
import { useThreadShells } from "../state/entities";
import { useDeskWidthStore } from "./cleoWide";
import { DockAccountControls } from "./DockAccountControls";
import { governedWorldViewBlocksLeaf } from "./WorldViewStateFace";
import { DeskDock } from "./DeskDock";
import { GovernedSessionBirthHost } from "./GovernedSessionBirthHost";
import { WorldSessionStrip } from "./WorldSessionStrip";

export interface NeucleosRoomBoundary {
  readonly authority: "local" | "world";
  readonly root: {
    readonly environmentId: string;
    readonly threadId: string;
  } | null;
  readonly rootKey: string | null;
  readonly sessionId: string | null;
  readonly state: "addressed" | "unavailable" | "unresolved";
}

const NeucleosRoomBoundaryContext = createContext<NeucleosRoomBoundary | null>(null);

export function useNeucleosRoomBoundary(): NeucleosRoomBoundary | null {
  return useContext(NeucleosRoomBoundaryContext);
}

/** Exported for the FD6 story: the room frame renders whole in a test. */
export function BoundaryFrame({
  boundary,
  children,
  rootStrip = null,
}: {
  readonly boundary: NeucleosRoomBoundary;
  readonly children: ReactNode;
  /**
   * A root tab row stacked ABOVE the boundary, spanning the whole window —
   * the ONE seat for level-0 tabs in BOTH modes (principal ruling 2026-08-28,
   * superseding the 2026-08-27 ego-mast seat): every tab row owns everything
   * beneath it, recursively, including the root. The Cleo Panel's top touches
   * this row's bottom, so switching a root tab swaps the panel and the whole
   * territory below it wholesale, exactly as a nested row swaps its subtree.
   */
  readonly rootStrip?: ReactNode;
}): ReactElement {
  return (
    <div
      className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-cleo-room-stack=""
    >
      {rootStrip}
      <NeucleosRoomBoundaryContext.Provider value={boundary}>
        <div
          className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden [transform:translateZ(0)]"
          data-cleo-room-boundary=""
          data-cleo-room-boundary-state={boundary.state}
          data-cleo-session-id={boundary.sessionId ?? undefined}
          data-cleo-room-root={boundary.rootKey ?? undefined}
          key={boundary.rootKey ?? boundary.state}
        >
          {/* FD6 (register FD-CAN-06): the room frame — the content frame the
              root row's active tab fuses into; it wraps BOTH modes' content
              (the boundary's face, not a mode's) and keeps its rounded foot.
              DATED 2026-09-01 (FD-CAN-28/29/30): the frame's own accent stroke
              yields to the cards' rings, and its token-glass ground moved to
              the frame row's underlay (data-rail-card-host) so the ringed
              cards paint ABOVE the ground and below every door and body; the
              frame itself paints nothing. Forced colors keep an honest plain
              border. */}
          <div
            className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-b-[19.5px] forced-colors:border forced-colors:border-t-0 forced-colors:border-[ButtonBorder]"
            data-cleo-room-frame=""
          >
            {children}
          </div>
        </div>
      </NeucleosRoomBoundaryContext.Provider>
    </div>
  );
}

function LocalSessionFrameContents({ children }: { readonly children: ReactNode }): ReactElement {
  const { projection } = useRootSessionStripProjection();
  const rootKey =
    projection.address.kind === "grounded" || projection.address.kind === "orphaned"
      ? (projection.address.keys[0] ?? null)
      : null;
  const rootTab =
    rootKey === null
      ? null
      : (projection.strips
          .find((row) => row.level === 0)
          ?.tabs.find((tab) => tab.key === rootKey) ?? null);
  const state: NeucleosRoomBoundary["state"] =
    rootKey !== null
      ? "addressed"
      : projection.state === "world-unresolved"
        ? "unresolved"
        : "unavailable";
  const boundary = useMemo<NeucleosRoomBoundary>(
    () => ({
      authority: "local",
      root:
        rootTab === null
          ? null
          : { environmentId: rootTab.environmentId, threadId: rootTab.threadId },
      rootKey,
      sessionId: null,
      state,
    }),
    [rootKey, rootTab, state],
  );
  return (
    // RM-CAN-14: Local's root tabs moved into the Room Rail, as a World's did
    // (FD-CAN-15/18); no strip is seated above the room.
    <BoundaryFrame boundary={boundary}>{children}</BoundaryFrame>
  );
}

function WorldSessionFrameContents({ children }: { readonly children: ReactNode }): ReactElement {
  const { state } = useWorldView();
  const shells = useThreadShells();
  const rootThread =
    state.kind === "world-ready"
      ? (shells.find(
          (shell) => shell.worldKey === state.worldKey && shell.sessionId === state.activeSessionId,
        ) ?? null)
      : null;
  const boundary = useMemo<NeucleosRoomBoundary>(
    () => ({
      authority: "world",
      root:
        rootThread === null
          ? null
          : { environmentId: rootThread.environmentId, threadId: rootThread.id },
      rootKey: state.kind === "world-ready" ? `${state.worldKey}:${state.activeSessionId}` : null,
      sessionId: state.kind === "world-ready" ? state.activeSessionId : null,
      state:
        state.kind === "world-ready"
          ? "addressed"
          : state.kind === "world-unavailable"
            ? "unavailable"
            : "unresolved",
    }),
    [rootThread, state],
  );
  return (
    <BoundaryFrame boundary={boundary} rootStrip={<WorldSessionStrip scope="root" />}>
      {children}
    </BoundaryFrame>
  );
}

function SessionFrameSwitch({ children }: { readonly children: ReactNode }): ReactElement {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { state } = useWorldView();
  if (state.kind === "local") {
    if (worldViewFrameKind(pathname) !== "session") return <>{children}</>;
    return (
      <RootSessionStripProjectionProvider>
        <LocalSessionFrameContents>{children}</LocalSessionFrameContents>
      </RootSessionStripProjectionProvider>
    );
  }
  if (pathname !== "/" && worldViewFrameKind(pathname) !== "session") return <>{children}</>;
  return <WorldSessionFrameContents>{children}</WorldSessionFrameContents>;
}

export function NeucleosNestedSessionStrip(): ReactElement | null {
  const { state } = useWorldView();
  // RM-CAN-14: Local's nested rows and its "+New Child" verb live on the Room
  // Rail (a row's children nest under it; the row carries the new-child seat).
  return state.kind === "local" ? null : <WorldSessionStrip scope="nested" />;
}

/**
 * World mode uses authoritative Sessions; signed-out Local mode uses local
 * threads. TR1: the room provider is seated above the palette in __root —
 * this frame consumes the context it once mounted.
 *
 * The left dock spans the World view. Account doors live in the right
 * context dock, with a bottom-right fallback when no session rail renders.
 */
// FD31 (register FD-CAN-31; the principal's word on the live Desk against the
// design: "that inner empty space obviously isn't correct. I can see different
// shades"): FD6's ROOM_GROUND retired dated. It painted a second full-room
// gradient over the shell's radial ground, flattening it and making the gaps
// between cards, the gutter, and the top bar three tones. The shell is the
// one ground; the underlay host paints nothing.

// The bottom-right account column's `w-12`, shown only when no context rail renders.
const ACCOUNT_FALLBACK_PX = 48;

export function NeucleosRoomFrame({ children }: { readonly children: ReactNode }): ReactElement {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { state } = useWorldView();
  const hasContextRail =
    worldViewFrameKind(pathname) === "session" && !governedWorldViewBlocksLeaf(state, pathname);
  // Widen Cleo's one desk budget (dated 2026-10-07): the plan reads the whole
  // row the furniture shares — dock, center and context — so folding the dock
  // never changes the width the plan was made from. The fallback account
  // column is not furniture the plan can move.
  const rowRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = rowRef.current;
    const { setFrameWidth } = useDeskWidthStore.getState();
    if (node === null || typeof ResizeObserver === "undefined") return;
    const publish = (contentWidth: number) => {
      const width = Math.floor(contentWidth) - (hasContextRail ? 0 : ACCOUNT_FALLBACK_PX);
      setFrameWidth(width > 0 ? width : null);
    };
    // Measured once before the first paint, so a desk opened with Widen on
    // paints its plan at once instead of the unwidened layout first.
    const style = getComputedStyle(node);
    publish(
      node.getBoundingClientRect().width -
        parseFloat(style.paddingLeft || "0") -
        parseFloat(style.paddingRight || "0"),
    );
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined) publish(entry.contentRect.width);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      setFrameWidth(null);
    };
  }, [hasContextRail]);
  return (
    <>
      {/* FD-CAN-33: the panel row is inset 14px from the shell's (the principal's dotted lines 2026-09-02: dock chips at the header's 24 = 14 + the dock's 10, one 14px rhythm with the cards)
          sides — the dock no longer hugs the shell edge (where the vignette
          band sat under its chips). Dated 2026-09-02 (the principal: "put
          the 14 back"): the same 14 above the row — the strip stood on the
          shell's rounded top edge with 0 while every other edge had 14, and
          the raised active tab needs the air to keep its silhouette. */}
      <div
        className="relative flex h-full min-h-0 min-w-0 px-[14px] pt-[14px]"
        data-desk-dock-row=""
        ref={rowRef}
      >
        {/* FD-CAN-29: the ringed cards' underlay — RailCard portals its glass
            and outline layers here, the row's FIRST child, so a carve crosses
            its column's clipping into the dock beside it and paints beneath
            every positioned door and body (no z-index spent). FD31: the host
            paints NO ground of its own — the shell's radial gradient is the
            one ground the cards float on (FD6's room glass retired dated). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          data-rail-card-host=""
        />
        <DeskDock />
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <SessionFrameSwitch>{children}</SessionFrameSwitch>
        </div>
        {hasContextRail ? null : (
          <div
            // DATED 2026-09-17: the 30 foot (14 + the card's 16 radius) of both
            // docks, so the account seat stands on the card's corner tangent.
            className="relative flex h-full w-12 shrink-0 flex-col items-center justify-end gap-2.5 px-2.5 pb-[30px]"
            data-context-account-fallback=""
          >
            <DockAccountControls wide={false} />
          </div>
        )}
      </div>
      <GovernedSessionBirthHost />
    </>
  );
}
